<template>
    <div>
      <!-- Header Section with Welcome Banner -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="welcome-banner rounded-4 p-4 bg-gradient shadow">
            <div class="row align-items-center">
              <div class="col-lg-7">
                <div class="d-flex align-items-center">
                  <div class="welcome-icon rounded-circle me-3 d-flex align-items-center justify-content-center">
                    <vue-feather type="user" stroke="#ffffff" size="24"></vue-feather>
                  </div>
                  <div>
                    <h3 class="mb-0 fw-bold">Welcome back, {{ profile.first_name }}</h3>
                    <p class="mb-0 text-muted">Here's your payroll dashboard overview</p>
                  </div>
                </div>
              </div>
              <div class="col-lg-5 mt-2 mt-md-3">
                <div class="filter-container p-2 bg-white bg-opacity-75 rounded-4">
                  <div class="mb-0 me-2 ms-2 text-center">
                    <span class="fw-bold">Last login: </span>
                    <br>
                    <span class="text-muted">{{ formatDate(profile.updated_at, true) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Links Section -->
      <div class="row mb-4" v-if="!isEmployee">
        <div class="col-12">
          <Card cardClasses="shadow border-0 h-100 rounded-4">
            <div class="d-flex justify-content-between align-items-center mb-4">
              <div class="d-flex align-items-center">
                <vue-feather type="grid" stroke="#6944a3" size="24"></vue-feather>
                <h5 class="mb-0 fw-bold text-muted ms-3">Quick Links</h5>
              </div>
            </div>
            <div class="row g-4">
              <div v-if="profile.role !== 'user'" class="col-sm-6 col-md-3">
                <div class="quick-link rounded-4 h-100" @click="addEmployeePage()">
                  <div class="d-flex flex-column align-items-center p-4">
                    <div class="quick-link-icon quick-link-icon-primary rounded-4 mb-3 d-flex align-items-center justify-content-center">
                      <vue-feather type="users" stroke="#ffffff" size="24"></vue-feather>
                    </div>
                    <h6 class="mb-0 text-center fw-bold">Add Employee</h6>
                    <p class="text-muted small text-center mb-0 mt-2">Add new employees to your organization</p>
                  </div>
                </div>
              </div>
              <div v-if="profile.role !== 'user'" class="col-sm-6 col-md-3">
                <div class="quick-link rounded-4 h-100" @click="$router.push({path: 'add-casual'})">
                  <div class="d-flex flex-column align-items-center p-4">
                    <div class="quick-link-icon quick-link-icon-secondary rounded-4 mb-3 d-flex align-items-center justify-content-center">
                      <vue-feather type="users" stroke="#ffffff" size="24"></vue-feather>
                    </div>
                    <h6 class="mb-0 text-center fw-bold">Add Casual</h6>
                    <p class="text-muted small text-center mb-0 mt-2">Add casual workers to your organization</p>
                  </div>
                </div>
              </div>
              <div class="col-sm-6 col-md-3">
                <div class="quick-link rounded-4 h-100" @click="$router.push('/profile')">
                  <div class="d-flex flex-column align-items-center p-4">
                    <div class="quick-link-icon quick-link-icon-success rounded-4 mb-3 d-flex align-items-center justify-content-center">
                      <vue-feather type="user" stroke="#ffffff" size="24"></vue-feather>
                    </div>
                    <h6 class="mb-0 text-center fw-bold">My Profile</h6>
                    <p class="text-muted small text-center mb-0 mt-2">View and edit your profile information</p>
                  </div>
                </div>
              </div>
              <div v-if="profile.role !== 'user' && profile.role !== 'admin'" class="col-sm-6 col-md-3">
                <div class="quick-link rounded-4 h-100" @click="$router.push('/users')">
                  <div class="d-flex flex-column align-items-center p-4">
                    <div class="quick-link-icon quick-link-icon-warning rounded-4 mb-3 d-flex align-items-center justify-content-center">
                      <vue-feather type="user-plus" stroke="#ffffff" size="24"></vue-feather>
                    </div>
                    <h6 class="mb-0 text-center fw-bold">Add User</h6>
                    <p class="text-muted small text-center mb-0 mt-2">Add new users to the system</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <!-- Charts Section -->
      <div v-if="!isEmployee">
        <div v-if="loading" class="text-center py-4">
          <Spinner spinner-class="mb-1" />
        </div>
        <GrossBarChart
            v-else
            :title="chartTitle"
            :data="this.analytics"
        ></GrossBarChart>
      </div>
      <div v-else>
        <div class="col-12 mb-4">
          <div class="d-flex bg-white shadow-lg p-3 rounded-3 justify-content-between">
            <h3 class="fw-bold">
              <vue-feather type="bar-chart-2" stroke="#652aa3" size="28" class="me-2"></vue-feather>
              Report Summary for {{ profile.first_name + ' ' + profile.last_name }}
            </h3>
          </div>
        </div>

        <div class="col-12 mb-4">
          <div class="card shadow-sm border-0">
            <div class="card-body bg-white rounded-3">
              <h5 class="card-title mb-3">
                <vue-feather type="filter" stroke="#652aa3" size="20" class="me-2"></vue-feather>
                Filters
              </h5>
              <div class="row g-3">
                <!-- Month From -->
                <div class="col-md-3">
                  <label for="month-from" class="form-label">Month From</label>
                  <select id="month-from" class="form-select shadow-sm" v-model="filters.month_from" @change="getAnalytics" data="">
                    <option value="" disabled>Select Month</option>
                    <option v-for="(month, index) in months" :key="index" :value="month.value">{{ month.text }}</option>
                  </select>
                </div>

                <!-- Year From -->
                <div class="col-md-3">
                  <label for="year-from" class="form-label">Year From</label>
                  <select id="year-from" class="form-select shadow-sm" v-model="filters.year_from" @change="getAnalytics" data="">
                    <option value="">Select Year</option>
                    <option v-for="year in analytics.years" :key="year" :value="year">{{ year }}</option>
                  </select>
                </div>

                <!-- Month To -->
                <div class="col-md-3">
                  <label for="month-to" class="form-label">Month To</label>
                  <select id="month-to" class="form-select shadow-sm" v-model="filters.month_to" @change="getAnalytics" data="">
                    <option value="" disabled>Select Month</option>
                    <option v-for="(month, index) in months" :key="index" :value="month.value">{{ month.text }}</option>
                  </select>
                </div>

                <!-- Year To -->
                <div class="col-md-3">
                  <label for="year-to" class="form-label">Year To</label>
                  <select id="year-to" class="form-select shadow-sm" v-model="filters.year_to" @change="getAnalytics" data="">
                    <option value="">Select Year</option>
                    <option v-for="year in analytics.years" :key="year" :value="year">{{ year }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <div class="row mb-4">
              <!-- Chart Area -->
              <div>
                <div v-if="loading" class="text-center py-4">
                  <Spinner spinner-class="mb-1" />
                </div>
                <div v-else>
                  <!-- Monthly Gross Bar Chart -->
                  <GrossBarChart
                      title="Payroll Summary"
                      :data="this.analytics"
                  ></GrossBarChart>

                  <!-- Monthly Deductions Bar Chart -->
                  <DeductionsBarChart
                      :data="this.analytics"
                      title="Deductions Summary"
                  >
                  </DeductionsBarChart>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<script>
    import analyticsService from "@/api/analyticsService";
    import GrossBarChart from "@/components/charts/GrossBarChart.vue";
    import formatNumber from "../utils/formatNumber";
    import formatDate from "../utils/formatDate";
    import {decrypt} from "@/utils/crypto";
    import 'intasend-inlinejs-sdk';
    import VueFeather from 'vue-feather';
    import DeductionsBarChart from "@/components/charts/DeductionsBarChart.vue";
    import employeeService from "@/api/employeeService";

    export default {
      name: "Dashboard",
      emits:['confirm', 'cancel'],
      components: {DeductionsBarChart, GrossBarChart, VueFeather },
      data() {
        return {
          loading: true,
          analytics: {
            data: {
              months: [],
              grossDataset: [],
            },
            grossTotal: 0
          },
          year: new Date().getFullYear(),
          years: [],
          profile: {},
          employeesNo: 0,
          subscription: {},
          organization: {},
          isEmployee: false,
          months: [
            {'value': '01', 'text': 'January'},
            {'value': '02', 'text': 'February'},
            {'value': '03', 'text': 'March'},
            {'value': '04', 'text': 'April'},
            {'value': '05', 'text': 'May'},
            {'value': '06', 'text': 'June'},
            {'value': '07', 'text': 'July'},
            {'value': '08', 'text': 'August'},
            {'value': '09', 'text': 'September'},
            {'value': '10', 'text': 'October'},
            {'value': '11', 'text': 'November'},
            {'value': '12', 'text': 'December'},
          ],
          filters: {
            month_from: '01',
            year_from: new Date().getFullYear(),
            month_to: this.currentMonth(),
            year_to: new Date().getFullYear(),
          }
        }
      },

      methods: {
        formatDate,
        formatNumber,
        getAnalytics: async function () {
          if (!this.isEmployee) {
            this.analytics = await analyticsService.getAnalytics(this.year)
          } else {
            let filters = {
              employee_id: this.profile.employee_id,
              from: `${this.filters.year_from}-${this.filters.month_from}-01`,
              to: `${this.filters.year_to}-${this.filters.month_to}-01`,
            }
            this.analytics = await analyticsService.getMonthlyAnalytics(filters)
          }
        },
        getEmployeesNo: async function () {
          if (sessionStorage.getItem('employeesNo') !== null) {
            this.employeesNo = JSON.parse(decrypt(sessionStorage.getItem('employeesNo')))
          } else {
            let employees = await employeeService.getEmployees();
            this.employeesNo = employees.length
          }
        },
        addEmployeePage: function () {
          if (this.subscription.employees <= this.employeesNo) {
            this.$router.push('/upgrade')
          } else {
            this.$router.push('/add-employee')
          }
        },
        currentMonth: function() {
          const date = new Date();
          let month = date.getMonth() + 1;
          return month.toString().padStart(2, '0');
        },
      },

      created: async function () {
        this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
        this.isEmployee = this.profile.role === 'employee'
        this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
        this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
        await this.getAnalytics()
        await this.getEmployeesNo()
        this.loading = false
        this.years = this.analytics.years
      },

      mounted: async function () {
        new window.IntaSend({
          publicAPIKey: process.env.VUE_APP_INTASEND_API_KEY,
          live: process.env.NODE_ENV !== 'development',
          redirectURL: this.redirectUrl
        })
      },

      computed: {
        chartTitle() {
          return `Payroll Annual Summary ${this.year}`
        }
      }
    }
</script>

<style scoped>
/* Card and general styles */
.card {
  transition: all 0.3s ease;
  border-radius: 1rem;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.1) !important;
}

.text-primary {
  color: #652aa3 !important;
}

/* Welcome Banner */
.welcome-banner {
  background: #652aa3; /* Fallback color */
  background: linear-gradient(135deg, #652aa3 0%, #a32a4f 100%);
  position: relative;
  overflow: hidden;
}

.welcome-banner::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  bottom: -50%;
  left: -50%;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
  transform: rotate(25deg);
  pointer-events: none;
}

.welcome-icon {
  width: 50px;
  height: 50px;
  background-color: rgba(119, 114, 114, 0.2);
}

.filter-container {
  backdrop-filter: blur(5px);
}

/* Quick Links */
.quick-link {
  background-color: #ffffff;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.quick-link:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.1) !important;
  border-color: rgba(101, 42, 163, 0.2);
}

.quick-links-icon {
  width: 36px;
  height: 36px;
  background-color: #652aa3;
}

.quick-link-icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quick-link-icon-primary {
  background-color: #652aa3;
}

.quick-link-icon-secondary {
  background-color: #a32a4f;
}

.quick-link-icon-success {
  background-color: #2aa367;
}

.quick-link-icon-warning {
  background-color: #c2b721;
}
</style>
